import React, { useState, useEffect } from 'react';
import { Timesheet, timesheetsApi, UpdateTimesheetData } from '../../api/timesheetsApi';
import { taskTypesApi } from '../../api/taskTypesApi';
import { Button } from '../../components/common/Button';
import { Icons } from '../../components/common/Icons';
import { formatDate, formatDateTime } from '../../utils/helpers';

interface TaskTypeOption {
  id: number;
  name: string;
}

interface EditTimesheetModalProps {
  timesheet: Timesheet;
  onSave: () => void;
  onClose: () => void;
}

export function EditTimesheetModal({ timesheet, onSave, onClose }: EditTimesheetModalProps) {
  const [hours, setHours] = useState(timesheet.hours);
  const [taskTypeId, setTaskTypeId] = useState(timesheet.task_type_id);
  const [description, setDescription] = useState(timesheet.description || '');
  const [taskTypes, setTaskTypes] = useState<TaskTypeOption[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    taskTypesApi.getTaskTypes().then(res => {
      setTaskTypes(res.tasks.map(t => ({ id: t.id, name: t.name })));
    }).catch(() => {});
  }, []);

  const handleSave = async () => {
    setError(null);
    setSaving(true);
    try {
      const data: UpdateTimesheetData = {};
      if (hours !== timesheet.hours) data.hours = hours;
      if (taskTypeId !== timesheet.task_type_id) data.task_type_id = taskTypeId;
      if (description !== (timesheet.description || '')) data.description = description;

      if (Object.keys(data).length === 0) {
        onClose();
        return;
      }

      await timesheetsApi.updateTimesheet(timesheet.id, data);
      onSave();
    } catch (err: any) {
      setError(err?.message || 'Failed to update timesheet');
    } finally {
      setSaving(false);
    }
  };

  const hourOptions: number[] = [];
  for (let h = 0.5; h <= 24; h += 0.5) hourOptions.push(h);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal edit-ts-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Edit Timesheet</h3>
          <button className="modal-close" onClick={onClose}><Icons.X /></button>
        </div>
        <div className="modal-body">
          {error && <p className="edit-ts-error">{error}</p>}

          <div className="edit-ts-info">
            <span><strong>Employee:</strong> {timesheet.employee_name}</span>
            <span><strong>Project:</strong> {timesheet.project_name}</span>
            <span><strong>Date:</strong> {formatDate(timesheet.date)}</span>
            <span><strong>Created At:</strong> {timesheet.created_at ? formatDateTime(timesheet.created_at) : '—'}</span>
            <span><strong>Updated At:</strong> {timesheet.updated_at ? formatDateTime(timesheet.updated_at) : '—'}</span>
          </div>

          <div className="edit-ts-fields">
            <label className="form-label">
              Hours
              <select
                value={hours}
                onChange={e => setHours(parseFloat(e.target.value))}
                className="edit-ts-select"
              >
                {hourOptions.map(h => (
                  <option key={h} value={h}>{h}h</option>
                ))}
              </select>
            </label>

            <label className="form-label">
              Task Type
              <select
                value={taskTypeId}
                onChange={e => setTaskTypeId(parseInt(e.target.value, 10))}
                className="edit-ts-select"
              >
                {taskTypes.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </label>

            <label className="form-label">
              Description
              <textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Enter description..."
                rows={3}
                className="edit-ts-textarea"
              />
            </label>
          </div>
        </div>
        <div className="modal-footer">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default EditTimesheetModal;
